// openEHR AM model — BMM package org.openehr.am.aom2.terminology
// Generated by tasks/split_model_by_bmm_packages.ts from tasks/bmm_package_map.json.
// Package boundaries follow the BMM package structure; edit the class bodies here,
// but re-run the splitter rather than hand-moving declarations between packages.

import type { ARCHETYPE } from "./archetype.ts";
import * as openehr_base from "../../base/mod.ts";

/**
 * Representation of any coded entity (term or constraint) in the archetype ontology.
 */
export class ARCHETYPE_TERM {
  /**
   * Internal storage for code
   * @protected
   */
  protected _code?: openehr_base.String;

  /**
   * Code of this term.
   */
  get code(): string | undefined {
    return this._code?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for code.
   * Use this to access openehr_base.String methods.
   */
  get $code(): openehr_base.String | undefined {
    return this._code;
  }

  /**
   * Sets code from either a primitive value or openehr_base.String wrapper.
   */
  set code(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._code = undefined;
    } else if (typeof val === "string") {
      this._code = openehr_base.String.from(val);
    } else {
      this._code = val;
    }
  }

  /**
   * Hash of keys (“text”, “description” etc) and corresponding values.
   */
  items?: undefined;
  /**
   * List of all keys used in this term.
   * @returns Result value
   */
  keys(): openehr_base.String {
    // TODO: Implement keys behavior
    // This will be covered in Phase 3 (see ROADMAP.md)
    throw new Error("Method keys not yet implemented.");
  }
}

/**
 * Class whose instances represent any kind of 1:N relationship between a source term and 1-N target terms.
 */
export abstract class TERMINOLOGY_RELATION {
  /**
   * Internal storage for id
   * @protected
   */
  protected _id?: openehr_base.String;

  /**
   * Code of source term of this relation.
   */
  get id(): string | undefined {
    return this._id?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for id.
   * Use this to access openehr_base.String methods.
   */
  get $id(): openehr_base.String | undefined {
    return this._id;
  }

  /**
   * Sets id from either a primitive value or openehr_base.String wrapper.
   */
  set id(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._id = undefined;
    } else if (typeof val === "string") {
      this._id = openehr_base.String.from(val);
    } else {
      this._id = val;
    }
  }

  /**
   * List of target terms in this relation.
   */
  members?: undefined;
}

/**
 * Representation of a flat value set within the archetype terminology.
 */
export class VALUE_SET extends TERMINOLOGY_RELATION {
}

/**
 * Local terminology of an archetype. This class defines the semantics of the terminology of an archetype.
 */
export class ARCHETYPE_TERMINOLOGY {
  /**
   * Internal storage for is_differential
   * @protected
   */
  protected _is_differential?: openehr_base.Boolean;

  /**
   * True if this terminology only contains terms relating to a differential specialisation of the owning artefact, rather than a complete set.
   */
  get is_differential(): boolean | undefined {
    return this._is_differential?.value;
  }

  /**
   * Gets the openehr_base.Boolean wrapper object for is_differential.
   * Use this to access openehr_base.Boolean methods.
   */
  get $is_differential(): openehr_base.Boolean | undefined {
    return this._is_differential;
  }

  /**
   * Sets is_differential from either a primitive value or openehr_base.Boolean wrapper.
   */
  set is_differential(val: boolean | openehr_base.Boolean | undefined) {
    if (val === undefined || val === null) {
      this._is_differential = undefined;
    } else if (typeof val === "boolean") {
      this._is_differential = openehr_base.Boolean.from(val);
    } else {
      this._is_differential = val;
    }
  }

  /**
   * Internal storage for original_language
   * @protected
   */
  protected _original_language?: openehr_base.String;

  /**
   * Original language of the terminology, as set at artefact creation or parsing time; must be a code in the ISO 639-1 2 character language code-set.
   */
  get original_language(): string | undefined {
    return this._original_language?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for original_language.
   * Use this to access openehr_base.String methods.
   */
  get $original_language(): openehr_base.String | undefined {
    return this._original_language;
  }

  /**
   * Sets original_language from either a primitive value or openehr_base.String wrapper.
   */
  set original_language(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._original_language = undefined;
    } else if (typeof val === "string") {
      this._original_language = openehr_base.String.from(val);
    } else {
      this._original_language = val;
    }
  }

  /**
   * Internal storage for concept_code
   * @protected
   */
  protected _concept_code?: openehr_base.String;

  /**
   * Term code defining the meaning of the artefact as a whole, and always used as the id-code on the root node of the artefact. Must be defined in the \`_term_definitions_\` property.
   */
  get concept_code(): string | undefined {
    return this._concept_code?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for concept_code.
   * Use this to access openehr_base.String methods.
   */
  get $concept_code(): openehr_base.String | undefined {
    return this._concept_code;
  }

  /**
   * Sets concept_code from either a primitive value or openehr_base.String wrapper.
   */
  set concept_code(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._concept_code = undefined;
    } else if (typeof val === "string") {
      this._concept_code = openehr_base.String.from(val);
    } else {
      this._concept_code = val;
    }
  }

  /**
   * Directory of term definitions as a two-level table. The outer hash keys are language codes, e.g. \`"en"\`, \`"de"\`, while the inner hash keys are term codes, e.g. \`"id17"\`, \`"at4"\`.
   */
  term_definitions?: undefined;
  /**
   * Directory of bindings to external terminology codes and value sets, as a two-level table. The outer hash keys are terminology ids, e.g. \`"SNOMED_CT"\`, and the inner hash keys are constraint codes, e.g. \`"at4"\`, \`"ac13"\` or paths. The indexed \`Uri\` objects represent references to externally defined resources, either terms, ontology concepts, or terminology subsets / ref-sets.
   */
  term_bindings?: undefined;
  /**
   * Archetype that owns this terminology.
   */
  owner_archetype?: ARCHETYPE;
  /**
   * Archetype-local value sets, each keyed by value-set id, i.e. an ac-code.
   */
  value_sets?: undefined;
  /**
   * Directory of extracts of external terminologies, as a two-level table. The outer hash keys are terminology ids, e.g. \`"SNOMED_CT"\`, while the inner hash keys are term codes or code-phrases from the relevant terminology, e.g. \`"10094842"\`.
   */
  terminology_extracts?: undefined;
  /**
   * Specialisation depth of this artefact. Unspecialised artefacts have depth 0, with each additional level of specialisation adding 1 to the specialisation_depth.
   * @returns Result value
   */
  specialisation_depth(): openehr_base.Integer {
    // TODO: Implement specialisation_depth behavior
    // This will be covered in Phase 3 (see ROADMAP.md)
    throw new Error("Method specialisation_depth not yet implemented.");
  }

  /**
   * List of all id codes in the terminology., i.e. the 'id' codes in an ADL archetype, which are the \`_node_id_\` values on \`C_OBJECT\` descendants.
   * @returns Result value
   */
  id_codes(): openehr_base.String {
    // TODO: Implement id_codes behavior
    // This will be covered in Phase 3 (see ROADMAP.md)
    throw new Error("Method id_codes not yet implemented.");
  }

  /**
   * List of all value term codes in the terminology, i.e. the 'at' codes in an ADL archetype, which are used as possible values on terminological constrainer nodes.
   * @returns Result value
   */
  value_codes(): openehr_base.String {
    // TODO: Implement value_codes behavior
    // This will be covered in Phase 3 (see ROADMAP.md)
    throw new Error("Method value_codes not yet implemented.");
  }

  /**
   * List of all value set codes in the terminology defining value sets. These correspond to the 'ac' codes in an ADL archetype.
   * @returns Result value
   */
  value_set_codes(): openehr_base.String {
    // TODO: Implement value_set_codes behavior
    // This will be covered in Phase 3 (see ROADMAP.md)
    throw new Error("Method value_set_codes not yet implemented.");
  }

  /**
   * True if language \`_a_lang_\` is present in archetype terminology.
   * @param a_lang - Parameter
   * @returns Result value
   */
  has_language(a_lang: openehr_base.String): openehr_base.Boolean {
    // TODO: Implement has_language behavior
    // This will be covered in Phase 3 (see ROADMAP.md)
    throw new Error("Method has_language not yet implemented.");
  }

  /**
   * True if there are bindings to terminology \`_a_terminology_id_\` present.
   * @param a_terminology_id - Parameter
   * @returns Result value
   */
  has_terminology(a_terminology_id: openehr_base.String): openehr_base.Boolean {
    // TODO: Implement has_terminology behavior
    // This will be covered in Phase 3 (see ROADMAP.md)
    throw new Error("Method has_terminology not yet implemented.");
  }

  /**
   * True if code \`_a_code_\` defined in this terminology.
   * @param a_code - Parameter
   * @returns Result value
   */
  has_term_code(a_code: openehr_base.String): openehr_base.Boolean {
    // TODO: Implement has_term_code behavior
    // This will be covered in Phase 3 (see ROADMAP.md)
    throw new Error("Method has_term_code not yet implemented.");
  }

  /**
   * Term definition for a code, in a specified language.
   * @param a_lang - Parameter
   * @param a_code - Parameter
   * @returns Result value
   */
  term_definition(
    a_lang: openehr_base.String,
    a_code: openehr_base.String,
  ): ARCHETYPE_TERM {
    // TODO: Implement term_definition behavior
    // This will be covered in Phase 3 (see ROADMAP.md)
    throw new Error("Method term_definition not yet implemented.");
  }

  /**
   * Binding of constraint corresponding to \`_a_code_\` in target external terminology \`_a_terminology_id_\`, as a string, which is usually a formal query expression.
   * @param a_terminology - Parameter
   * @param a_code - Parameter
   * @returns Result value
   */
  term_binding(
    a_terminology: openehr_base.String,
    a_code: openehr_base.String,
  ): openehr_base.Uri {
    // TODO: Implement term_binding behavior
    // This will be covered in Phase 3 (see ROADMAP.md)
    throw new Error("Method term_binding not yet implemented.");
  }

  /**
   * List of terminologies to which term or constraint bindings exist in this terminology, computed from bindings.
   * @returns Result value
   */
  terminologies_available(): openehr_base.String {
    // TODO: Implement terminologies_available behavior
    // This will be covered in Phase 3 (see ROADMAP.md)
    throw new Error("Method terminologies_available not yet implemented.");
  }

  /**
   * Return an \`ARCHETYPE_TERM\` from specified terminology extract, for specified term code.
   * @param a_terminology_id - Parameter
   * @param a_code - Parameter
   * @returns Result value
   */
  terminology_extract_term(
    a_terminology_id: openehr_base.String,
    a_code: openehr_base.String,
  ): ARCHETYPE_TERM {
    // TODO: Implement terminology_extract_term behavior
    // This will be covered in Phase 3 (see ROADMAP.md)
    throw new Error("Method terminology_extract_term not yet implemented.");
  }

  /**
   * True if there is a provided terminology extract present.
   * @param a_terminology_id - Parameter
   * @returns Result value
   */
  has_terminology_extract(
    a_terminology_id: openehr_base.String,
  ): openehr_base.Boolean {
    // TODO: Implement has_terminology_extract behavior
    // This will be covered in Phase 3 (see ROADMAP.md)
    throw new Error("Method has_terminology_extract not yet implemented.");
  }

  /**
   * List of languages in which terms in this terminology are available.
   * @returns Result value
   */
  languages_available(): openehr_base.String {
    // TODO: Implement languages_available behavior
    // This will be covered in Phase 3 (see ROADMAP.md)
    throw new Error("Method languages_available not yet implemented.");
  }
}
